import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function AuthCallback() {
  const { exchangeGoogleSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "?")).get("session_id");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    const processSession = async () => {
      // Retry up to 2 times in case of transient errors
      for (let attempt = 0; attempt < 2; attempt++) {
        const result = await exchangeGoogleSession(sessionId);
        if (result.success) {
          // Clear the hash and redirect
          window.history.replaceState(null, "", window.location.pathname);
          navigate(result.user?.role === "admin" ? "/admin" : "/dashboard", { replace: true });
          return;
        }
        if (attempt === 0) {
          // Wait briefly before retry
          await new Promise(r => setTimeout(r, 1000));
        } else {
          console.error("Google auth exchange failed after retries:", result.error);
          setError(result.error);
          setTimeout(() => navigate("/login", { replace: true }), 3000);
        }
      }
    };

    processSession();
  }, [exchangeGoogleSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-4">
        {error ? (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-red-600 text-lg font-bold">!</span>
            </div>
            <p className="text-sm text-red-600 text-center max-w-sm" data-testid="auth-callback-error">{error}</p>
            <p className="text-xs text-muted-foreground">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="w-8 h-8 border-2 border-[#0A3FA3] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Authenticating...</p>
          </>
        )}
      </div>
    </div>
  );
}
